import type { UnknownRecord } from './types';

const MAX_DEPTH = 4;
const MAX_ARRAY_ITEMS = 20;
const MAX_OBJECT_KEYS = 40;
const MAX_STRING_LENGTH = 500;

const SENSITIVE_KEY_PARTS = [
  'password',
  'pass',
  'pwd',
  'token',
  'authorization',
  'cookie',
  'secret',
  'signature',
  'session',
  'otp',
  'captcha',
  'phone',
  'mobile',
  'email',
  'inn',
  'iban',
  'account',
  'card',
  'passport',
  'fullName',
  'full_name',
];

export const isRecord = (value: unknown): value is UnknownRecord => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export const getString = (value: unknown): string | undefined => {
  return typeof value === 'string' && value.trim() ? value : undefined;
};

export const getNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && !Number.isNaN(Number(value))) return Number(value);
  return undefined;
};

export const getCollectionValues = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  if (isRecord(value)) return Object.values(value);
  return [];
};

export const compactRecord = (value: Record<string, unknown>): Record<string, unknown> | undefined => {
  const entries = Object.entries(value).filter(([, entryValue]) => {
    if (entryValue === undefined || entryValue === null || entryValue === '') return false;
    if (Array.isArray(entryValue)) return entryValue.length > 0;
    if (isRecord(entryValue)) return Object.keys(entryValue).length > 0;
    return true;
  });

  return entries.length ? Object.fromEntries(entries) : undefined;
};

const truncateString = (value: string): string => {
  if (value.length <= MAX_STRING_LENGTH) return value;
  return `${value.slice(0, MAX_STRING_LENGTH)}...[truncated ${value.length - MAX_STRING_LENGTH} chars]`;
};

const sanitizeValue = (value: unknown, depth = 0, seen = new WeakSet<object>()): unknown => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return truncateString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();

  if (value instanceof Date) return value.toISOString();
  if (value instanceof Error) {
    return compactRecord({
      name: value.name,
      message: value.message,
      stack: value.stack ? truncateString(value.stack) : undefined,
    });
  }

  if (depth >= MAX_DEPTH) return '[MaxDepth]';

  if (Array.isArray(value)) {
    const sanitizedArray = value.slice(0, MAX_ARRAY_ITEMS).map((item) => sanitizeValue(item, depth + 1, seen));

    if (value.length > MAX_ARRAY_ITEMS) {
      sanitizedArray.push(`[${value.length - MAX_ARRAY_ITEMS} more items]`);
    }

    return sanitizedArray;
  }

  if (!isRecord(value)) return String(value);
  if (seen.has(value)) return '[Circular]';
  seen.add(value);

  const entries = Object.entries(value).slice(0, MAX_OBJECT_KEYS);
  const sanitizedEntries = entries.map(([key, entryValue]) => {
    const loweredKey = key.toLowerCase();
    if (SENSITIVE_KEY_PARTS.some((part) => loweredKey.includes(part.toLowerCase()))) {
      return [key, '[Filtered]'] as const;
    }
    return [key, sanitizeValue(entryValue, depth + 1, seen)] as const;
  });

  if (Object.keys(value).length > MAX_OBJECT_KEYS) {
    sanitizedEntries.push(['__truncated_keys', Object.keys(value).length - MAX_OBJECT_KEYS]);
  }

  return Object.fromEntries(sanitizedEntries);
};

export const sanitizePayload = (payload: unknown): unknown => {
  if (typeof payload === 'string') {
    const trimmedValue = payload.trim();
    if (!trimmedValue.startsWith('{') && !trimmedValue.startsWith('[')) return truncateString(payload);

    try {
      return sanitizeValue(JSON.parse(payload) as unknown);
    } catch {
      return truncateString(payload);
    }
  }

  return sanitizeValue(payload);
};
